import {
  RequestTimelineEventType,
  type DeliveryState,
  type EnvelopeDetailResponse,
  type SenderAdoptionResponse,
} from '@signbento/contracts'
import type { AccountTransport } from './purchases.js'

/**
 * Sent-request status — S5.4 (§6.1, RMT-07/AT-012, DEC-01).
 *
 * What the sender needs after sending, without inventing anything: the authoritative step states,
 * the deadlines, and — for a **manual sequential** request — which capability link is authorized
 * right now, so the next person can be given their link without a managed email. A managed request
 * queues its own invitations, so the affordance is absent, and nothing here ever carries a link.
 *
 * The response is the service's; a malformed one is treated as "no status" rather than rendered.
 */

export interface RequestStep {
  readonly recipient_id: string
  readonly name: string
  readonly email: string | null
  readonly kind: 'signer' | 'cc'
  readonly state: string
  readonly delivery_state?: DeliveryState
  readonly order_index: number
  /**
   * The server-recorded first presentation of the document to this participant (§3.6): an
   * opened/viewed observation, never an email open, and null until it happens.
   */
  readonly viewed_at: string | null
  readonly submitted_at: string | null
  /** The sender's own step, which is adopted rather than signed through a link (D02). */
  readonly is_sender: boolean
  /** The compare-and-set token an operational edit quotes for this step (S5.5, STM-10). */
  readonly capability_version: number
  /** CC rows only: when this participant may read the committed artifact (RMT-12). */
  readonly read_access: 'NONE' | 'FINAL'
}

/**
 * One request timeline entry (§3.6): a server-recorded fact and the instant the server recorded
 * it. `recipient_id` names a step of the same status; the timeline itself never carries a name.
 */
export interface RequestTimelineEntry {
  readonly type: RequestTimelineEventType
  readonly occurred_at: string
  readonly recipient_id: string | null
  /** Recorded for the person who held this step before the sender corrected it. */
  readonly superseded: boolean
}

/**
 * The request detail with any timeline entry this client doesn't know removed, so a newer service
 * that adds a timeline type can't make an older client's strict parse of the whole detail fail.
 * Anything else is left exactly as received for the caller's own parse.
 */
export function withKnownTimeline(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const timeline = (raw as { readonly timeline?: unknown }).timeline
  if (!Array.isArray(timeline)) return raw
  return {
    ...raw,
    timeline: timeline.filter(
      (event: unknown) =>
        typeof event === 'object' &&
        event !== null &&
        RequestTimelineEventType.safeParse((event as { readonly type?: unknown }).type).success,
    ),
  }
}

/** A server instant in the reader's locale and time zone; an absent or unreadable one is "—". */
export function formatLocalDate(iso: string | null | undefined, locale: string): string {
  if (iso === null || iso === undefined) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

/**
 * A server instant as a short local moment, the style rows and timelines share: "Oct 17, 12:25 AM",
 * with the year only when it isn't this year and no zone name (the reader's own is implied, as on
 * every other screen; the audit certificate is where the exact UTC instant lives).
 */
export function formatLocalMoment(
  iso: string | null | undefined,
  locale: string,
  now: Date = new Date(),
): string {
  if (iso === null || iso === undefined) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/** A server instant as a short local day ("Oct 12" this year, "Oct 12, 2027" otherwise). */
export function formatLocalDay(
  iso: string | null | undefined,
  locale: string,
  now: Date = new Date(),
): string {
  if (iso === null || iso === undefined) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date)
}

/**
 * Whole local days left until an instant, rounded up so "in 1 day" covers the last hours too;
 * zero or less once it has passed.
 */
export function daysUntil(iso: string, now: Date = new Date()): number {
  return Math.ceil((Date.parse(iso) - now.getTime()) / (24 * 60 * 60 * 1000))
}

/** The step whose link the sender may reveal next, or null when nothing is authorized. */
export function nextLinkStep(status: RequestStatus): RequestStep | null {
  if (status.next_link_ready === null) return null
  return (
    status.steps.find((step) => step.recipient_id === status.next_link_ready?.recipient_id) ?? null
  )
}

export interface RequestStatus {
  readonly envelope_id: string
  readonly title: string
  readonly state: string
  readonly delivery_mode: string
  readonly routing_mode: string
  readonly recipient_count: number
  readonly submitted_count: number
  /** Signers and CC recipients are counted separately, never summed (RMT-12). */
  readonly required_signer_count: number
  readonly cc_recipient_count: number
  /** The envelope's operational revision: the token an extension quotes (STM-10). */
  readonly lock_version: number
  /** The moment the request became active: the ceiling for an extension is measured from here. */
  readonly activated_at: string | null
  readonly steps: readonly RequestStep[]
  /** The step whose link is authorized now (manual routing only); never a link, just who. */
  readonly next_link_ready: { readonly recipient_id: string; readonly order_index: number } | null
  readonly signing_deadline: string | null
  readonly finalization_deadline?: string | null
  /**
   * Until when the service keeps the encrypted signed copy (DEV-008). After it only the copy the
   * sender saved, or the one this device kept, remains.
   */
  readonly download_deadline?: string | null
  /**
   * The deadline the request was given, kept for a closed request too ("Expired on Oct 12"):
   * `signing_deadline` is only sent while the request can still collect signatures.
   */
  readonly expires_at?: string | null
  readonly created_at?: string | null
  readonly completed_at?: string | null
  /** The sender's draft identity (`draft_<document id>`), when the service names it. */
  readonly draft_id?: string | null
  /** Sender-only: the reason the signer gave for declining, or the sender's own void reason. */
  readonly decline_reason?: string | null
  readonly void_reason?: string | null
  /** D10 counts only; null when the request asks for no attachment. */
  readonly attachments?: { readonly requested: number; readonly collected: number } | null
  /** Oldest first, as the service ordered it; empty when the service sent none. */
  readonly timeline: readonly RequestTimelineEntry[]
}

export type RequestStatusOutcome =
  | { readonly kind: 'STATUS'; readonly status: RequestStatus }
  | { readonly kind: 'MISSING' }
  | { readonly kind: 'FAILED'; readonly code: string }

export async function loadRequestStatus(options: {
  readonly transport: AccountTransport
  readonly envelope_id: string
}): Promise<RequestStatusOutcome> {
  try {
    const detail = await options.transport.request<EnvelopeDetailResponse>({
      method: 'GET',
      path: `/v1/envelopes/${encodeURIComponent(options.envelope_id)}`,
    })
    const envelope = detail?.envelope
    if (envelope === undefined || envelope === null) return { kind: 'MISSING' }
    return {
      kind: 'STATUS',
      status: {
        envelope_id: envelope.envelope_id,
        title: envelope.title,
        state: envelope.state,
        delivery_mode: envelope.delivery_mode,
        routing_mode: envelope.routing_mode,
        recipient_count: envelope.recipient_count,
        submitted_count: envelope.submitted_count,
        required_signer_count: envelope.required_signer_count ?? 0,
        cc_recipient_count: envelope.cc_recipient_count ?? 0,
        lock_version: envelope.lock_version ?? 0,
        activated_at: envelope.activated_at ?? null,
        steps: (detail.recipients ?? []).map((recipient) => ({
          recipient_id: recipient.recipient_id,
          name: recipient.name,
          email: recipient.email ?? null,
          kind: recipient.kind,
          state: recipient.state,
          delivery_state: recipient.delivery_state,
          order_index: recipient.order_index,
          viewed_at: recipient.viewed_at ?? null,
          submitted_at: recipient.submitted_at ?? null,
          is_sender: recipient.is_sender === true,
          capability_version: recipient.capability_version,
          read_access: recipient.read_access ?? 'NONE',
        })),
        next_link_ready:
          detail.next_link_ready === undefined
            ? null
            : {
                recipient_id: detail.next_link_ready.recipient_id,
                order_index: detail.next_link_ready.order_index,
              },
        signing_deadline: detail.deadlines?.signing_deadline ?? null,
        finalization_deadline: detail.deadlines?.finalization_deadline ?? null,
        download_deadline: detail.deadlines?.download_deadline ?? null,
        expires_at: envelope.expires_at ?? null,
        created_at: envelope.created_at ?? null,
        completed_at: envelope.completed_at ?? null,
        draft_id: envelope.draft_id ?? null,
        decline_reason: detail.decline_reason ?? null,
        void_reason: detail.void_reason ?? null,
        attachments:
          detail.attachments === undefined
            ? null
            : {
                requested: detail.attachments.requested,
                collected: detail.attachments.collected,
              },
        timeline: (
          (withKnownTimeline(detail) as Pick<EnvelopeDetailResponse, 'timeline'>).timeline ?? []
        ).map((event) => ({
          type: event.type,
          occurred_at: event.occurred_at,
          recipient_id: event.recipient_id ?? null,
          superseded: event.superseded === true,
        })),
      },
    }
  } catch (error) {
    const code = (error as { code?: string }).code ?? 'OP_UNEXPECTED'
    return { kind: 'FAILED', code }
  }
}

/**
 * Whether the sender's own step is due: `sender_order: AFTER` puts the sender last, and their step
 * is an **adoption** of the frozen payload rather than a bearer link (D02). Routing changes the
 * step from BLOCKED to ACTIVE when its predecessors finish; only then may the sender adopt.
 */
export function senderAdoptionDue(input: { readonly status: RequestStatus }): boolean {
  if (input.status.state !== 'ACTIVE') return false
  const sender = input.status.steps.find((step) => step.is_sender)
  if (sender === undefined || sender.kind !== 'signer' || sender.state !== 'ACTIVE') return false
  const earlier = input.status.steps.filter(
    (step) => step.kind === 'signer' && step.order_index < sender.order_index,
  )
  // Every step ahead of the sender must be accepted — the same rule the service enforces in its
  // transaction, so a premature call would only be refused.
  return earlier.every((step) => step.state === 'SUBMITTED')
}

export type SenderAdoptionOutcome =
  | { readonly kind: 'ADOPTED'; readonly response: SenderAdoptionResponse }
  | { readonly kind: 'NOT_DUE'; readonly code: string }
  | { readonly kind: 'FAILED'; readonly code: string }

/** Records the sender's own step once the chain reaches it. Idempotency is the caller's key. */
export async function adoptSenderStep(options: {
  readonly transport: AccountTransport
  readonly envelope_id: string
  readonly display_name: string
  readonly now: string
  readonly id_factory: () => string
}): Promise<SenderAdoptionOutcome> {
  const client_request_id = options.id_factory()
  try {
    const response = await options.transport.request<SenderAdoptionResponse>({
      method: 'POST',
      path: `/v1/envelopes/${encodeURIComponent(options.envelope_id)}/sender-adoption`,
      body: {
        client_request_id,
        adopted: true,
        display_name: options.display_name,
        adopted_at: options.now,
      },
      client_request_id,
    })
    return { kind: 'ADOPTED', response }
  } catch (error) {
    const code = (error as { code?: string }).code ?? 'OP_UNEXPECTED'
    // "Not your turn yet", "already adopted" and "the request ended" are states, not failures.
    const notDue =
      code === 'OP_FORBIDDEN' ||
      code === 'RECIPIENT_ALREADY_SUBMITTED' ||
      code === 'ENVELOPE_TERMINAL' ||
      code === 'OP_REVISION_MISMATCH'
    return notDue ? { kind: 'NOT_DUE', code } : { kind: 'FAILED', code }
  }
}
